import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeMode = "light" | "dark";
/** What the user picked; "system" follows the OS `prefers-color-scheme`. */
export type ThemePreference = ThemeMode | "system";

type ThemeContextValue = {
  /** Resolved mode that is actually applied. */
  theme: ThemeMode;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = "edicta-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

function applyTheme(mode: ThemeMode) {
  document.documentElement.setAttribute("data-theme", mode);
}

function systemTheme(): ThemeMode {
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

function resolve(preference: ThemePreference): ThemeMode {
  return preference === "system" ? systemTheme() : preference;
}

function readStoredPreference(): ThemePreference {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "dark" || stored === "light" || stored === "system" ? stored : "system";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const mode = resolve(preference);
    applyTheme(mode);
    return mode;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, preference);
    setTheme(resolve(preference));
    if (preference !== "system") return;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = () => setTheme(systemTheme());
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [preference]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setPreference = useCallback(
    (next: ThemePreference) => setPreferenceState(next),
    [],
  );
  const toggleTheme = useCallback(
    () => setPreferenceState(theme === "light" ? "dark" : "light"),
    [theme],
  );

  const value = useMemo(
    () => ({ theme, preference, setPreference, toggleTheme }),
    [theme, preference, setPreference, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
